import type { Task } from "idfweb-tasks";
import { Filetype, mkdirp, readFile, writeFile, type MarkedChanges, type Vfs } from "idfweb-vfs";

import { isDirectoryHandle, type DirectoryHandle, type FileHandle, type LocalFile } from "./directory.ts";
import { isExcludedProjectPath } from "./project.ts";

/**
 * Two-way sync between the project in the guest and a directory on the user's
 * computer. Paths here are relative to both roots, e.g. "main/main.c".
 *
 * A base record per file holds each side's modification time and size as of
 * the last sync. A pass compares both sides with the base: a file changed on
 * one side is copied to the other, and one changed on both is a conflict,
 * which only the user settles. Passes run one at a time; requests that arrive
 * during a pass are merged into the next one.
 */

/** The copy a conflict is settled in favour of. */
export type Side = "local" | "guest";

/** A file changed on both sides since the last sync, and whether each side still has it. */
export interface Conflict {
    path: string;
    local: boolean;
    guest: boolean;
}

export interface SyncOptions {
    vfs: Vfs;
    /** The tree in the guest, e.g. "/project/blink". */
    root: string;
    directory: DirectoryHandle;
    /** The side to keep for each conflict of a pass. A path left out stays as it is on both sides. */
    resolve(conflicts: Conflict[]): Promise<Map<string, Side>>;
    /** A copy that failed. The pass goes on, and the file is compared again in a later pass. */
    failed(path: string, error: unknown): void;
    /** A task for a pass that copies at least `PROGRESS_AT` files. */
    progress?(files: number): Task;
}

/**
 * What a directory being opened may hold, beyond which it is taken to be the
 * wrong one. The guest filesystem has two gigabytes for the toolchain, the
 * project and the build tree together.
 */
const MAX_FILES = 20_000;
const MAX_BYTES = 256 * 1024 * 1024;

/** How many copies a pass makes before it reports progress. */
const PROGRESS_AT = 20;

interface Stamp {
    time: number;
    size: number;
}

interface Base {
    local: Stamp;
    guest: Stamp;
}

export class DirectorySync {
    readonly #options: SyncOptions;
    readonly #base = new Map<string, Base>();
    #chain: Promise<void> = Promise.resolve();
    /** The paths the next pass compares, "" for everything, and that pass's promise. */
    #waiting: { scope: Set<string>; done: Promise<void> } | undefined;
    #closed = false;

    constructor(options: SyncOptions) {
        this.#options = options;
    }

    /**
     * Copy the directory into the guest project. `empty` empties the project
     * once the directory has been listed; a directory with more files or bytes
     * than a project could have is refused before that.
     */
    open(task?: Task, empty?: () => void): Promise<void> {
        return this.#after(() => this.#pass(new Set([""]), { task, empty }));
    }

    /** Compare everything on both sides. */
    rescan(): Promise<void> {
        return this.#request([""]);
    }

    /** Compare the local paths a watcher reported, or everything when it could not say which. */
    localChanged(paths: string[] | undefined): Promise<void> {
        if (paths === undefined) return this.#request([""]);
        const wanted = paths.filter((path) => path === "" || !isExcludedProjectPath(path));
        return wanted.length === 0 ? Promise.resolve() : this.#request(wanted);
    }

    /**
     * Compare the paths a guest change report may concern: the files it lists,
     * and the synced files that a directory it lists no longer holds.
     */
    guestChanged(changes: MarkedChanges): Promise<void> {
        const prefix = `${this.#options.root}/`;
        const wanted = new Set<string>();
        for (const file of changes.files) {
            if (!file.startsWith(prefix)) continue;
            const path = file.slice(prefix.length);
            if (!isExcludedProjectPath(path)) wanted.add(path);
        }
        const listed = new Map<string, Set<string>>();
        for (const { path, entries } of changes.directories) {
            listed.set(path, new Set(entries.map((entry) => entry.name)));
        }
        for (const path of this.#base.keys()) {
            if (this.#goneFromGuest(path, listed)) wanted.add(path);
        }
        return wanted.size === 0 ? Promise.resolve() : this.#request(wanted);
    }

    /** Stop: a pass in progress copies nothing more, and later requests do nothing. */
    close(): void {
        this.#closed = true;
    }

    #request(paths: Iterable<string>): Promise<void> {
        if (this.#waiting === undefined) {
            const scope = new Set<string>();
            const done = this.#after(() => {
                this.#waiting = undefined;
                return this.#pass(scope);
            });
            this.#waiting = { scope, done };
        }
        for (const path of paths) this.#waiting.scope.add(path);
        return this.#waiting.done;
    }

    #after(work: () => Promise<void>): Promise<void> {
        const done = this.#chain.then(work);
        this.#chain = done.catch(() => {});
        return done;
    }

    /** Compare the paths under `scope` on both sides and copy what changed on one. */
    async #pass(
        scope: Set<string>,
        opening?: { task: Task | undefined; empty: (() => void) | undefined },
    ): Promise<void> {
        if (this.#closed || scope.size === 0) return;
        const prefixes = scope.has("") ? [""] : [...scope];
        const local = new Map<string, LocalFile>();
        for (const prefix of prefixes) {
            await this.#listLocal(prefix, local, opening !== undefined);
        }
        if (this.#closed) return;
        if (opening !== undefined) {
            checkSize(this.#options.directory.name, local);
            opening.empty?.();
        }
        const guest = new Map<string, Stamp>();
        for (const prefix of prefixes) this.#listGuest(prefix, guest);

        const paths = new Set([...local.keys(), ...guest.keys()]);
        for (const path of this.#base.keys()) if (within(path, scope)) paths.add(path);
        const copies: { path: string; from: Side }[] = [];
        const conflicts: Conflict[] = [];
        for (const path of [...paths].sort()) {
            const found = local.get(path);
            const stamp = guest.get(path);
            const base = this.#base.get(path);
            const localMoved = base === undefined ? found !== undefined : !same(stampOf(found), base.local);
            const guestMoved = base === undefined ? stamp !== undefined : !same(stamp, base.guest);
            if (localMoved && guestMoved) {
                if (found === undefined && stamp === undefined) this.#base.delete(path);
                else if (
                    found !== undefined &&
                    stamp !== undefined &&
                    (await sameFile(found, readFile(this.#options.vfs, this.#guestPath(path))))
                ) {
                    this.#base.set(path, { local: stampOf(found)!, guest: stamp });
                } else {
                    conflicts.push({ path, local: found !== undefined, guest: stamp !== undefined });
                }
            } else if (localMoved) copies.push({ path, from: "local" });
            else if (guestMoved) copies.push({ path, from: "guest" });
        }

        const many = copies.length >= PROGRESS_AT;
        const task = opening?.task ?? (many ? this.#options.progress?.(copies.length) : undefined);
        task?.progress(0, { total: copies.length });
        for (const [at, { path, from }] of copies.entries()) {
            await this.#copy(path, from, local.get(path), guest.get(path));
            task?.progress(at + 1, { detail: path });
        }
        if (task !== opening?.task) task?.done();
        if (conflicts.length > 0) await this.#settle(conflicts);
    }

    /** Ask about the conflicts, then copy each chosen side as it is after the answer. */
    async #settle(conflicts: Conflict[]): Promise<void> {
        const choices = await this.#options.resolve(conflicts);
        for (const [path, side] of choices) {
            if (this.#closed) return;
            const local = new Map<string, LocalFile>();
            await this.#listLocal(path, local, false);
            const guest = new Map<string, Stamp>();
            this.#listGuest(path, guest);
            await this.#copy(path, side, local.get(path), guest.get(path));
        }
    }

    async #copy(path: string, from: Side, found: LocalFile | undefined, stamp: Stamp | undefined): Promise<void> {
        if (this.#closed) return;
        try {
            if (from === "local") await this.#toGuest(path, found, stamp);
            else await this.#toLocal(path, found, stamp);
        } catch (e) {
            this.#options.failed(path, e);
        }
    }

    /** Make the guest's copy the local one, leaving the guest file alone when the bytes match. */
    async #toGuest(path: string, found: LocalFile | undefined, stamp: Stamp | undefined): Promise<void> {
        const { vfs } = this.#options;
        const at = this.#guestPath(path);
        if (found === undefined) {
            if (stamp !== undefined) vfs.unlink(at);
            this.#base.delete(path);
            await this.#pruneGuest(parent(path));
            return;
        }
        const bytes = new Uint8Array(await found.arrayBuffer());
        if (this.#closed) return;
        if (stamp === undefined || stamp.size !== bytes.length || !equal(readFile(vfs, at), bytes)) {
            mkdirp(vfs, this.#guestPath(parent(path)));
            writeFile(vfs, at, bytes);
        }
        this.#base.set(path, { local: stampOf(found)!, guest: this.#guestStamp(at)! });
    }

    /** Make the local copy the guest's, leaving the local file alone when the bytes match. */
    async #toLocal(path: string, found: LocalFile | undefined, stamp: Stamp | undefined): Promise<void> {
        const at = this.#guestPath(path);
        if (stamp === undefined) {
            if (found !== undefined) await this.#removeLocal(path);
            this.#base.delete(path);
            await this.#pruneLocal(parent(path));
            return;
        }
        // Stamped before it is read, so that a write in between is seen as a change next time.
        const guest = this.#guestStamp(at);
        // Deleted since the listing, as a build that unpacks a component again does;
        // the deletion's change report brings the next pass.
        if (guest === undefined) return;
        const bytes = readFile(this.#options.vfs, at);
        let file = found;
        if (file === undefined || !(await sameFile(file, bytes))) {
            if (this.#closed) return;
            let handle: FileHandle;
            try {
                handle = await this.#localFile(path, true);
                const writable = await handle.createWritable();
                await writable.write(bytes);
                await writable.close();
            } catch (e) {
                // A file this write created would be taken for a local change next time.
                if (found === undefined) await this.#removeLocal(path).catch(() => {});
                throw e;
            }
            file = await handle.getFile();
        }
        this.#base.set(path, { local: stampOf(file)!, guest });
    }

    /** The files at or under `prefix` in the directory, with a snapshot of each. */
    async #listLocal(prefix: string, into: Map<string, LocalFile>, limited: boolean): Promise<void> {
        const names = parts(prefix);
        let dir: DirectoryHandle | undefined = this.#options.directory;
        let handle: DirectoryHandle | FileHandle | undefined = dir;
        if (names.length > 0) {
            dir = await this.#localDirectory(names.slice(0, -1).join("/"), false).catch(() => undefined);
            handle = dir && (await entryIn(dir, names.at(-1)!));
        }
        if (handle === undefined) return;
        if (!isDirectoryHandle(handle)) {
            into.set(prefix, await handle.getFile());
            return;
        }
        for await (const [name, entry] of handle.entries()) {
            const path = prefix === "" ? name : `${prefix}/${name}`;
            if (isExcludedProjectPath(path)) continue;
            if (isDirectoryHandle(entry)) await this.#listLocal(path, into, limited);
            else into.set(path, await entry.getFile());
            if (limited && into.size > MAX_FILES) {
                throw new Error(`there are more than ${MAX_FILES} project files under ${path}`);
            }
        }
    }

    /** The regular files at or under `prefix` in the guest project. */
    #listGuest(prefix: string, into: Map<string, Stamp>): void {
        const { vfs } = this.#options;
        const at = this.#guestPath(prefix);
        let filetype: Filetype;
        try {
            filetype = vfs.stat(at, false).filetype;
        } catch {
            return;
        }
        if (filetype === Filetype.REGULAR_FILE) into.set(prefix, this.#guestStamp(at)!);
        if (filetype !== Filetype.DIRECTORY) return;
        for (const entry of vfs.readdir(at)) {
            const path = prefix === "" ? entry.name : `${prefix}/${entry.name}`;
            if (!isExcludedProjectPath(path)) this.#listGuest(path, into);
        }
    }

    /**
     * Whether the guest no longer has `path`, going by the change report's
     * listings. Its nearest listed ancestor decides, because everything above a
     * change is listed and a directory below one is unchanged or gone.
     */
    #goneFromGuest(path: string, listed: Map<string, Set<string>>): boolean {
        const { root } = this.#options;
        for (let child = `${root}/${path}`; child !== root;) {
            const slash = child.lastIndexOf("/");
            const dir = child.slice(0, slash) || "/";
            const names = listed.get(dir);
            if (names !== undefined) return !names.has(child.slice(slash + 1));
            child = dir;
        }
        return false;
    }

    #guestPath(path: string): string {
        return path === "" ? this.#options.root : `${this.#options.root}/${path}`;
    }

    #guestStamp(at: string): Stamp | undefined {
        try {
            const stat = this.#options.vfs.stat(at, false);
            return { time: stat.mtimeMs, size: stat.size };
        } catch {
            return undefined;
        }
    }

    async #localDirectory(path: string, create: boolean): Promise<DirectoryHandle> {
        let dir = this.#options.directory;
        for (const name of parts(path)) dir = await dir.getDirectoryHandle(name, { create });
        return dir;
    }

    async #localFile(path: string, create: boolean): Promise<FileHandle> {
        const dir = await this.#localDirectory(parent(path), create);
        return dir.getFileHandle(parts(path).at(-1)!, { create });
    }

    async #removeLocal(path: string): Promise<void> {
        const dir = await this.#localDirectory(parent(path), false);
        await dir.removeEntry(parts(path).at(-1)!);
    }

    /** Remove the guest directories left empty by a deletion, up to one the directory still has. */
    async #pruneGuest(path: string): Promise<void> {
        const { vfs } = this.#options;
        for (; path !== ""; path = parent(path)) {
            const at = this.#guestPath(path);
            if (vfs.readdir(at).length > 0) return;
            if ((await this.#localDirectory(path, false).catch(() => undefined)) !== undefined) return;
            if (this.#closed) return;
            vfs.rmdir(at);
        }
    }

    /** Remove the local directories left empty by a deletion, up to one the guest still has. */
    async #pruneLocal(path: string): Promise<void> {
        for (; path !== ""; path = parent(path)) {
            const dir = await this.#localDirectory(path, false).catch(() => undefined);
            if (dir === undefined) return;
            for await (const _ of dir.entries()) return;
            if (this.#guestStamp(this.#guestPath(path)) !== undefined) return;
            await this.#removeLocal(path);
        }
    }
}

function stampOf(file: LocalFile | undefined): Stamp | undefined {
    return file && { time: file.lastModified, size: file.size };
}

async function sameFile(file: LocalFile, bytes: Uint8Array): Promise<boolean> {
    return file.size === bytes.length && equal(new Uint8Array(await file.arrayBuffer()), bytes);
}

function same(a: Stamp | undefined, b: Stamp | undefined): boolean {
    return a === undefined || b === undefined ? a === b : a.time === b.time && a.size === b.size;
}

function equal(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

/** Whether `path` is one of `scope`'s paths or below one. */
function within(path: string, scope: Set<string>): boolean {
    if (scope.has("")) return true;
    for (let at = path; at !== ""; at = parent(at)) if (scope.has(at)) return true;
    return false;
}

function checkSize(name: string, local: Map<string, LocalFile>): void {
    let bytes = 0;
    for (const file of local.values()) bytes += file.size;
    if (bytes > MAX_BYTES) {
        throw new Error(`${name} holds more than ${MAX_BYTES / 1024 / 1024} MB of project files`);
    }
}

async function entryIn(dir: DirectoryHandle, name: string): Promise<DirectoryHandle | FileHandle | undefined> {
    try {
        return await dir.getDirectoryHandle(name);
    } catch {
        return dir.getFileHandle(name).catch(() => undefined);
    }
}

function parts(path: string): string[] {
    return path.split("/").filter((part) => part !== "");
}

function parent(path: string): string {
    const slash = path.lastIndexOf("/");
    return slash < 0 ? "" : path.slice(0, slash);
}
